import { CameraAccessorDef, CameraOptions, CameraPhoto, LogManager } from "@mcleod/core";
import { MobileDeviceCameraDef, CameraResultType, CameraSource, CameraPermissionStatus } from "./MobileDeviceCameraDef";
import { CapacitorAccess } from "../capacitor";
import { MobileDeviceAccessor } from "../device";

const log = LogManager.getLogger("common.MobileDeviceCameraAccessor");

/**
 * Implementation of CameraAccessorDef using Capacitor's Camera plugin.
 */
export class MobileDeviceCameraAccessor implements CameraAccessorDef {
    private static _instance: MobileDeviceCameraAccessor;

    private constructor() {}

    public static get instance(): MobileDeviceCameraAccessor {
        if (this._instance == null) {
            this._instance = new MobileDeviceCameraAccessor();
        }
        return this._instance;
    }

    private get plugin(): MobileDeviceCameraDef | null {
        return CapacitorAccess.plugins?.Camera;
    }

    /**
     * Returns true if the camera plugin is available.
     */
    public get hasCamera(): boolean {
        if (MobileDeviceAccessor.isAppleSimulator) {
            return false; // iOS Simulator doesn't have simulated camera.
        }
        return this.plugin !== undefined;
    }

    /**
     * Check camera permissions.
     * @returns Promise resolving to current permission status
     */
    public async checkPermissions(): Promise<CameraPermissionStatus> {
        if (!this.plugin) {
            return { camera: 'denied' };
        }

        const status = await this.plugin.checkPermissions();
        return { camera: this.mapPermissionState(status.camera) };
    }

    /**
     * Request camera permissions.
     * @returns Promise resolving to updated permission status
     */
    public async requestPermissions(): Promise<CameraPermissionStatus> {
        if (!this.plugin) {
            return { camera: 'denied' };
        }

        const status = await this.plugin.requestPermissions({ permissions: ['camera'] });
        return { camera: this.mapPermissionState(status.camera) };
    }

    /**
     * Take a photo using the device camera.
     * Will check and request permissions if needed.
     * @param options Optional camera settings
     * @returns Promise resolving to the captured photo, or undefined if canceled/denied
     */
    public async takePhoto(options?: CameraOptions): Promise<CameraPhoto | undefined> {
        if (!this.plugin) {
            log.error("CameraPlugin is not available.");
            throw new Error('Camera is not available');
        }

        // Check current permissions
        let permissionStatus = await this.checkPermissions();

        // Request permissions if not granted
        if (permissionStatus.camera === 'prompt') {
            permissionStatus = await this.requestPermissions();
        }

        // If still not granted, return undefined
        if (permissionStatus.camera === 'denied') {
            log.info("Camera permissions have been denied by user.");
            throw new Error('Camera permissions are denied. Enable permission for this app in OS Settings.');
        }

        try {
            const photo = await this.plugin.getPhoto({
                quality: options?.quality ?? 50,
                allowEditing: false,
                resultType: CameraResultType.Uri,
                source: CameraSource.Camera,
                saveToGallery: false,
                width: options?.width,
                height: options?.height,
                correctOrientation: true
            });

            return {
                base64String: photo.base64String,
                dataUrl: photo.dataUrl,
                path: photo.path,
                webPath: photo.webPath,
                format: photo.format
            };
        } catch (error) {
            // User canceled or other error
            log.error('Failed to take photo:', error);
            throw new Error(`Failed to take photo: ${
                error instanceof Error ? error.message : "an unknown error has occurred."
            }`);
        }
    }

    /**
     * Maps Capacitor permission state to our simplified permission state.
     */
    private mapPermissionState(state: string): CameraPermissionStatus['camera'] {
        switch (state) {
            case 'granted':
                return 'granted';
            case 'denied':
                return 'denied';
            case 'limited':
                return 'limited';
            case 'prompt':
            case 'prompt-with-rationale':
            default:
                return 'prompt';
        }
    }
}


